import * as PIXI from "pixi.js";

export class RangeSlider extends PIXI.Container {

    private readonly DEFAULT_ANCHOR_POINT: any = new PIXI.Point(0.5, 0.5);

    private subscribers: Array<Function> = [];
    private quantizedValues: Array<number> = [];
    private sliderPoint: PIXI.Sprite;
    private sliderBackground: PIXI.Sprite;
    private sliderActiveBackgroundMask: PIXI.Graphics;
    private pointerDown = false;
    private currentValue: number;
    private pointerBaseTexture: PIXI.Texture;
    private pointerDownTexture: PIXI.Texture;
    private label: PIXI.Text = new PIXI.Text();
    private showBelowActiveBackground = true;

    constructor(pointerTexturePath: string,
                pointerDownTexturePath: string,
                rangeBackgroundTexturePath: string,
                rangeActiveBackgroundTexturePath: string,
                private minRangeValue: number = 0,
                private maxRangeValue: number = 100) {
        super();
        this.pointerBaseTexture = PIXI.Texture.fromFrame(pointerTexturePath);
        this.pointerDownTexture = PIXI.Texture.fromFrame(pointerDownTexturePath);
        if (minRangeValue > maxRangeValue) {
            throw new Error("Maximum range lower than minimum range passed");
        }

        this.currentValue = minRangeValue;
        this.createAndAddSliderRange(rangeBackgroundTexturePath);
        this.createMaskSliderActiveBackgroundMask(rangeActiveBackgroundTexturePath);
        this.createSliderPointer();
    }

    public setValuesQuantization(quantizedValues: Array<number>): void {
        for (let i = 1; i < quantizedValues.length; i++) {
            if (quantizedValues[i - 1] > quantizedValues[i]) {
                throw new Error("Values must be passed in ascending order!");
            }
        }
        if (quantizedValues[0] < this.minRangeValue) {
            throw new Error("Min quantized value lower than minimum slider");
        }
        if (quantizedValues[quantizedValues.length - 1] > this.maxRangeValue) {
            throw new Error("Max quantized value lower than maximum slider");
        }
        this.quantizedValues = quantizedValues;
    }

    public subscribeOnValueChanged(subscriber: Function): void {
        this.subscribers.push(subscriber);
    }

    public getValue(): number {
        return this.currentValue;
    }

    public setValue(value: number): void {
        const percentOfBar = (value / (this.maxRangeValue - this.minRangeValue)) * this.sliderBackground.width;
        this.sliderPoint.position.x = percentOfBar;
        this.sliderActiveBackgroundMask.width = percentOfBar;
        this.onValueChosen(false);
        this.currentValue = value;
        this.callAllSubscribers();
    }

    public setLabel(text: PIXI.Text): void {
        this.removeChild(this.label);
        text.text = "" + this.currentValue;
        this.label = text;
        this.addChild(this.label);
    }

    set belowActiveBackground(value: boolean) {
        this.showBelowActiveBackground = value;
        this.updateActiveBackgroundMask();
    }

    private onValueChosen(update: boolean = true): void {
        const value = Math.round(this.sliderPoint.position.x /
            this.sliderBackground.width * this.maxRangeValue);
        let offsetFromQuantizedValues = this.maxRangeValue, offsetOffValue;
        for (let i = 0; i < this.quantizedValues.length; i++) {
            offsetOffValue = Math.abs(this.quantizedValues[i] - value);
            if (offsetOffValue < offsetFromQuantizedValues) {
                offsetFromQuantizedValues = offsetOffValue;
                this.currentValue = this.quantizedValues[i];
            }
        }

        this.sliderPoint.position.x =
            this.sliderBackground.width * (this.currentValue / this.maxRangeValue);
        this.label.position.x = this.sliderPoint.position.x;
        this.label.text = "" + this.currentValue;

        if (update) {
            this.callAllSubscribers();
        }
    }

    private onSliderMoved(): void {
        const value = Math.round(this.sliderPoint.position.x /
            this.sliderBackground.width * this.maxRangeValue),
            previousValue = this.currentValue;
        let offsetFromQuantizedValues = this.maxRangeValue, offsetOffValue;
        for (let i = 0; i < this.quantizedValues.length; i++) {
            offsetOffValue = Math.abs(this.quantizedValues[i] - value);
            if (offsetOffValue < offsetFromQuantizedValues) {
                offsetFromQuantizedValues = offsetOffValue;
                this.currentValue = this.quantizedValues[i];
            }
        }

        this.label.position.x = this.sliderPoint.position.x;
        this.updateActiveBackgroundMask();
        this.label.text = "" + this.currentValue;

        if (this.currentValue !== previousValue) {
            this.callAllSubscribers();
        }
    }

    private updateActiveBackgroundMask(): void {
        if (this.showBelowActiveBackground) {
            this.sliderActiveBackgroundMask.position.x = 0;
            this.sliderActiveBackgroundMask.width = this.sliderPoint.position.x;
        } else {
            this.sliderActiveBackgroundMask.position.x = this.sliderPoint.position.x;
            this.sliderActiveBackgroundMask.width = this.sliderBackground.width;
        }
    }

    private createAndAddSliderRange(rangeBackgroundTexturePath: string): void {
        this.sliderBackground = new PIXI.Sprite(PIXI.Texture.fromFrame(rangeBackgroundTexturePath));
        this.sliderBackground.interactive = true;
        this.sliderBackground.buttonMode = true;
        this.sliderBackground.on("pointerdown", (event) => {
            this.pointerDown = true;
            this.sliderPoint.texture = this.pointerDownTexture;
            this.sliderPointerMoved(event.data.global.x);
            this.sliderPointerMoved(event.data.global.x);
            this.sliderPointerMoved(event.data.global.x);
            this.onSliderMoved();
        });
        this.sliderBackground.on("pointermove", (event) => {
            this.sliderPointerMoved(event.data.global.x);
            this.onSliderMoved();
        });
        this.sliderBackground.on("pointerup", () => {
            this.pointerDown = false;
            this.sliderPoint.texture = this.pointerBaseTexture;
            this.onValueChosen();
        });
        this.sliderBackground.on("pointercancel", () => {
            this.pointerDown = false;
            this.sliderPoint.texture = this.pointerBaseTexture;
            this.onValueChosen();
        });
        this.sliderBackground.on("pointerupoutside", () => {
            this.pointerDown = false;
            this.sliderPoint.texture = this.pointerBaseTexture;
            this.onValueChosen();
        });
        this.addChild(this.sliderBackground);
    }

    private createMaskSliderActiveBackgroundMask(rangeActiveBackgroundTexturePath: string): void {
        const sliderActiveBackground = new PIXI.Sprite(PIXI.Texture.fromFrame(rangeActiveBackgroundTexturePath));
        const sliderActiveBackgroundContainer = new PIXI.Container();
        sliderActiveBackgroundContainer.addChild(sliderActiveBackground);

        this.sliderActiveBackgroundMask = new PIXI.Graphics();
        this.sliderActiveBackgroundMask.beginFill(0xFFFFFF);
        this.sliderActiveBackgroundMask.drawRect(0, 0, 100, 500);
        this.sliderActiveBackgroundMask.endFill();
        sliderActiveBackgroundContainer.mask = this.sliderActiveBackgroundMask;
        sliderActiveBackgroundContainer.addChild(this.sliderActiveBackgroundMask);
        this.addChild(sliderActiveBackgroundContainer);
    }

    private createSliderPointer(): void {
        this.sliderPoint = new PIXI.Sprite(this.pointerBaseTexture);
        this.sliderPoint.interactive = true;
        this.sliderPoint.buttonMode = true;
        this.sliderPoint.anchor = this.DEFAULT_ANCHOR_POINT;
        this.sliderPoint.position.y = this.sliderBackground.height / 2;

        this.sliderPoint.on("pointerdown", () => {
            this.pointerDown = true;
            this.sliderPoint.texture = this.pointerDownTexture;
        });
        this.sliderPoint.on("pointermove", (event) => {
            this.sliderPointerMoved(event.data.global.x);
            this.onSliderMoved();
        });
        this.sliderPoint.on("pointerup", () => {
            this.pointerDown = false;
            this.sliderPoint.texture = this.pointerBaseTexture;
            this.onValueChosen();
        });
        this.sliderPoint.on("pointercancel", () => {
            this.pointerDown = false;
            this.sliderPoint.texture = this.pointerBaseTexture;
            this.onValueChosen();
        });
        this.sliderPoint.on("pointerupoutside", () => {
            this.pointerDown = false;
            this.sliderPoint.texture = this.pointerBaseTexture;
            this.onValueChosen();
        });

        this.addChild(this.sliderPoint);
    }

    private callAllSubscribers(): void {
        for (let i = 0; i < this.subscribers.length; i++) {
            this.subscribers[i](this.currentValue);
        }
    }

    private sliderPointerMoved(pointerPositionX: number): void {
        if (this.pointerDown) {
            const newPositionX =
                this.sliderPoint.position.x + pointerPositionX - this.sliderPoint.getGlobalPosition().x;
            if (newPositionX > this.sliderBackground.width) {
                this.sliderPoint.position.x = this.sliderBackground.width;
            } else if (newPositionX < 0) {
                this.sliderPoint.position.x = 0;
            } else {
                this.sliderPoint.position.x = newPositionX;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/controlElements/RangeSlider.ts